import { useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';


/**
 * Translate an ISO country row to its display label in the current
 * UI language.
 *
 * Originally lived inside `pages/CafRegister.jsx`. Extracted in 0.32.4
 * because the dial-codes admin page also needs translated country names
 * (it now displays one row per ISO country, mirroring the phone-prefix
 * picker), and duplicating the lookup logic would have been brittle.
 *
 * Lookup order:
 *   1. Translation key `country.{code}` in the current language (e.g.
 *      `country.am` in Armenian → "Հայաստան")
 *   2. The translation context's own fallback chain (any other loaded
 *      language) if the key resolves to anything other than the literal key
 *   3. The row's `name` column as the final, deterministic fallback
 *
 * Why fall back to `name` rather than letting `t()` return the bare
 * key: `name` is guaranteed populated for every ISO row, while the
 * literal "country.am" string would be a visible bug if shown to a user.
 *
 * The returned function takes a country-row-shaped object (`{ code, name }`).
 * Both the country library and the form-options endpoint return objects of
 * this shape, so the same hook serves both.
 */
export default function useCountryLabel() {
  const { t } = useLanguage();
  // useCallback-style stability: re-create the lookup function only when
  // `t` itself changes (which only happens on language switch or when
  // new translations finish loading). Without this, every render produces
  // a fresh function reference and useMemo consumers re-run unnecessarily.
  return useMemo(() => (c) => {
    if (!c) return '';
    const key = 'country.' + c.code;
    const translated = t(key);
    if (translated === key || !translated || !translated.trim()) {
      return c.name || c.code;
    }
    return translated;
  }, [t]);
}
